import { offlineSceneDirName, type OfflineSceneKey } from "@novorender/api";
import { useMemo } from "react";

import { useAppSelector } from "app/redux-store-interactions";
import { useSceneId } from "hooks/useSceneId";
import { selectBackendProvider, selectProcoreContext } from "slices/explorer";

/**
 * What identifies the open scene's offline files.
 *
 * A bare scene id was enough while a scene had a single set of files, and is not once it
 * has several: a version and a slice of that version download different geometry, so both
 * belong in the key wherever the backend has them. Where those files then go is the SDK's
 * to decide — the read path derives the same directory from an asset url, so the two would
 * have to agree on a naming convention if the viewer named it here.
 *
 * Version and slice only on the Procore backend, where those ids exist. A Novorender scene
 * id already names one set of files, and rekeying it would orphan every record already in
 * storage. Procore records keyed the older way — by project id — are orphaned by this on
 * first load, and nothing migrates them: the files are re-downloaded under the new key.
 */
export function useOfflineSceneKey(): OfflineSceneKey {
    const sceneId = useSceneId();
    const procoreContext = useAppSelector(selectProcoreContext);
    const isProcoreBackendProvider = useAppSelector(selectBackendProvider) === "procore";
    // No version yet means init has not published the resolved one back to the store; the
    // scene id alone is then the whole identity, and every offline action happens after
    // init, so this is a guard rather than a case. The slice goes with it: a slice without
    // a version is not a key the SDK accepts, since it cannot be told from a version.
    const sceneVersionId = isProcoreBackendProvider ? procoreContext?.sceneVersionId : undefined;
    const sceneSliceId = sceneVersionId ? procoreContext?.sceneSliceId : undefined;

    // Kept stable across renders: this is a dependency of the effects that run the
    // offline actions, and a fresh object would re-run whatever they were in the middle of.
    return useMemo(() => ({ sceneId, sceneVersionId, sceneSliceId }), [sceneId, sceneVersionId, sceneSliceId]);
}

/**
 * The name the open scene's offline record goes under: its OPFS directory, its
 * localStorage entry and the `viewerScenes` entry listed inside it all share it.
 */
export function useOfflineSceneDirName(): string {
    return offlineSceneDirName(useOfflineSceneKey());
}
